import React from 'react';
import { useForwardWiringQueueStatus } from '../hooks/useForwardWiringQueueStatus';
import type {
  ForwardWiringQueueStatusEntry,
  ForwardWiringWorkerKillSwitch,
} from '../types/security';
import { EmptyState, ErrorState, LoadingSkeleton } from './LoadingSkeleton';

function isKillSwitchOn(state: ForwardWiringWorkerKillSwitch): boolean {
  return ['1', 'true', 'yes', 'on'].includes(
    state.FORWARD_WIRING_WORKERS_ENABLED.toLowerCase(),
  );
}

function WorkerStateBanner({
  state,
}: {
  state: ForwardWiringWorkerKillSwitch;
}): React.JSX.Element {
  const on = isKillSwitchOn(state);
  return (
    <div
      role="status"
      style={{
        padding: '0.75rem 1rem',
        border: '1px solid',
        borderColor: on
          ? 'var(--severity-low-border, #3b82f6)'
          : 'var(--severity-medium-border, #eab308)',
        background: on
          ? 'var(--severity-low-bg, #eff6ff)'
          : 'var(--severity-medium-bg, #fefce8)',
        color: on
          ? 'var(--severity-low-text, #1e40af)'
          : 'var(--severity-medium-text, #854d0e)',
        borderRadius: '0.375rem',
        marginBottom: '1rem',
      }}
    >
      <div style={{ fontWeight: 600 }}>
        Worker state: {on ? '🟢 ACTIVE — workers delivering' : '🟡 LOG-ONLY (kill switch off)'}
      </div>
      <div style={{ fontSize: '0.75rem', marginTop: '0.25rem' }}>
        FORWARD_WIRING_WORKERS_ENABLED = {state.FORWARD_WIRING_WORKERS_ENABLED}
      </div>
      <div style={{ fontSize: '0.75rem', marginTop: '0.25rem' }}>
        {state.interpretation}
      </div>
    </div>
  );
}

function QueueCountsCard({
  title,
  entry,
}: {
  title: string;
  entry: ForwardWiringQueueStatusEntry;
}): React.JSX.Element {
  const order = ['pending', 'processing', 'delivered', 'failed'];
  const statuses = Array.from(
    new Set([...order, ...Object.keys(entry.counts_by_status)]),
  );
  return (
    <div
      style={{
        padding: '1rem',
        border: '1px solid var(--border-default, #e5e7eb)',
        borderRadius: '0.5rem',
        background: 'var(--bg-secondary)',
      }}
    >
      <strong>{title}</strong>
      <table
        style={{ width: '100%', marginTop: '0.5rem', fontSize: '0.875rem' }}
        aria-label={`${title} counts by status`}
      >
        <tbody>
          {statuses.map((status) => (
            <tr key={status}>
              <td style={{ padding: '0.125rem 0', textTransform: 'capitalize' }}>
                {status}
              </td>
              <td style={{ textAlign: 'right', fontFamily: 'monospace' }}>
                {entry.counts_by_status[status] ?? 0}
              </td>
            </tr>
          ))}
          <tr style={{ borderTop: '1px solid var(--border-default, #e5e7eb)' }}>
            <td style={{ padding: '0.25rem 0', fontWeight: 600 }}>Total</td>
            <td style={{ textAlign: 'right', fontFamily: 'monospace', fontWeight: 600 }}>
              {entry.total}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function RecentRowsTable({
  title,
  entry,
}: {
  title: string;
  entry: ForwardWiringQueueStatusEntry;
}): React.JSX.Element {
  return (
    <div style={{ marginTop: '1rem' }}>
      <strong>{title} — recent rows</strong>
      {entry.recent_rows.length === 0 ? (
        <EmptyState message="No queue rows yet." />
      ) : (
        <table
          style={{
            width: '100%',
            marginTop: '0.5rem',
            fontSize: '0.875rem',
            borderCollapse: 'collapse',
          }}
          aria-label={`${title} recent rows`}
        >
          <thead>
            <tr style={{ textAlign: 'left' }}>
              <th style={{ padding: '0.25rem 0.5rem' }}>ID</th>
              <th style={{ padding: '0.25rem 0.5rem' }}>Skill</th>
              <th style={{ padding: '0.25rem 0.5rem' }}>Severity</th>
              <th style={{ padding: '0.25rem 0.5rem' }}>Status</th>
              <th style={{ padding: '0.25rem 0.5rem' }}>Submitted</th>
            </tr>
          </thead>
          <tbody>
            {entry.recent_rows.map((row) => (
              <tr
                key={row.id}
                style={{ borderTop: '1px solid var(--border-default, #e5e7eb)' }}
              >
                <td style={{ padding: '0.25rem 0.5rem', fontFamily: 'monospace' }}>
                  {row.id.slice(0, 8)}
                </td>
                <td style={{ padding: '0.25rem 0.5rem' }}>{row.originating_skill}</td>
                <td style={{ padding: '0.25rem 0.5rem' }}>{row.severity_token}</td>
                <td style={{ padding: '0.25rem 0.5rem' }}>{row.status}</td>
                <td style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem' }}>
                  {row.submitted_at
                    ? new Date(row.submitted_at).toLocaleString()
                    : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export function ForwardWiringStatusTab(): React.JSX.Element {
  const { data, isLoading, error, refetch } = useForwardWiringQueueStatus();

  if (isLoading && !data) return <LoadingSkeleton />;
  if (error) return <ErrorState error={error} onRetry={refetch} />;
  if (!data) return <EmptyState message="No queue data available." />;

  return (
    <div>
      <WorkerStateBanner state={data.worker_kill_switch_state} />
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '1rem',
        }}
      >
        <QueueCountsCard
          title="Orchestrator Prompt Push"
          entry={data.orchestrator_prompt_queue}
        />
        <QueueCountsCard
          title="Open Issues Handoff"
          entry={data.open_issues_queue}
        />
      </div>
      <RecentRowsTable
        title="Orchestrator Prompt Push"
        entry={data.orchestrator_prompt_queue}
      />
      <RecentRowsTable title="Open Issues Handoff" entry={data.open_issues_queue} />
    </div>
  );
}

export { isKillSwitchOn };
